import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { generateDocument } from '../api/documents';
import { configApi } from '../api/config';
import { schemasApi } from '../api/schemas';
import { templatesApi } from '../api/templates';
import { useAsyncData } from '../hooks/useAsyncData';
import { GuidedPayloadEditor } from './GuidedPayloadEditor';
import { PayloadViewer } from './PayloadViewer';
import { parseJson } from '../lib/fieldInference';
import { MARITIME_DOCUMENT_TYPE_OPTIONS, type DocumentType } from '../types/api';

type EntryMode = 'guided' | 'raw';

// Looks for a real, backend-persisted example payload for the selected document type — never
// fabricates one. Two sources, checked in order:
//  1. An ACTIVE schema's metadata.samplePayload — populated when a schema is created from a
//     sample via MessageSchemaManagerPage (see persistSchema in EaiConsolePages.tsx).
//  2. An active template's defaultSourcePayload, a real field on TemplateRecord meant for
//     exactly this purpose.
// If neither exists for the selected type, the UI says so explicitly rather than hiding the
// gap — see the "no example available" state below.
interface ExampleSource {
  kind: 'schema' | 'template';
  label: string;
  payload: string;
}

export function DocumentGenerateForm() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [documentType, setDocumentType] = useState<DocumentType>('CUSCAR');
  const [partnerId, setPartnerId] = useState('');
  const [referenceId, setReferenceId] = useState('');
  const [correlationId, setCorrelationId] = useState('');
  const [payloadText, setPayloadText] = useState('');
  const [mode, setMode] = useState<EntryMode>('guided');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: partners } = useAsyncData(() => configApi.partners.list({ page: 1, pageSize: 100, isActive: true }), []);
  const { data: schemaData } = useAsyncData(() => schemasApi.list({ lifecycleState: 'ACTIVE' }), []);
  const { data: templateData } = useAsyncData(() => templatesApi.list({ page: 1, pageSize: 100 }), []);

  const exampleSource = useMemo<ExampleSource | null>(() => {
    const schema = (schemaData?.data ?? []).find(
      (candidate) => candidate.entityType === documentType && typeof candidate.metadata?.['samplePayload'] === 'string',
    );
    if (schema) {
      return { kind: 'schema', label: `${schema.name}:${schema.version}`, payload: schema.metadata['samplePayload'] as string };
    }

    const template = (templateData?.data ?? []).find(
      (candidate) => candidate.documentType === documentType && candidate.defaultSourcePayload && Object.keys(candidate.defaultSourcePayload).length > 0,
    );
    if (template) {
      return { kind: 'template', label: template.name, payload: JSON.stringify(template.defaultSourcePayload, null, 2) };
    }

    return null;
  }, [documentType, schemaData, templateData]);

  const parsedPayload = parseJson(payloadText);
  const previewPayload = parsedPayload && typeof parsedPayload === 'object' ? parsedPayload : {};

  function applyExample() {
    if (exampleSource) setPayloadText(exampleSource.payload);
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    try {
      setIsSubmitting(true);
      if (!payloadText.trim()) {
        throw new Error('Payload JSON is required.');
      }
      const payload = JSON.parse(payloadText) as Record<string, unknown>;
      const created = await generateDocument({
        documentType,
        partnerId,
        referenceId,
        correlationId,
        payload,
      });
      navigate({ pathname: `../documents/${created.id}`, search });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Generation failed.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section className="panel">
      <div className="panel-header">
        <h2>Generate document</h2>
        <p>Create a document from a source payload for validation, troubleshooting, or partner certification.</p>
      </div>
      <form className="form-grid" onSubmit={handleSubmit}>
        <label>
          <span>Document type</span>
          <select value={documentType} onChange={(event) => setDocumentType(event.target.value as DocumentType)}>
            {MARITIME_DOCUMENT_TYPE_OPTIONS.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Partner</span>
          <select value={partnerId} required onChange={(event) => setPartnerId(event.target.value)}>
            <option value="">Select a partner</option>
            {(partners?.data ?? []).map((partner) => (
              <option key={partner.id} value={partner.id}>{partner.name} ({partner.code})</option>
            ))}
          </select>
        </label>
        <label>
          <span>Reference ID</span>
          <input required value={referenceId} onChange={(event) => setReferenceId(event.target.value)} />
        </label>
        <label>
          <span>Correlation ID</span>
          <input required value={correlationId} onChange={(event) => setCorrelationId(event.target.value)} />
        </label>

        <div className="full-width setup-callout">
          {exampleSource ? (
            <>
              <div>
                <strong>Example payload available</strong>
                <span>From {exampleSource.kind === 'schema' ? 'active schema' : 'active template'} {exampleSource.label}.</span>
              </div>
              <button type="button" className="ghost-button" onClick={applyExample}>Use example payload</button>
            </>
          ) : (
            <div>
              <strong>No example payload available for {documentType}</strong>
              <span>No active schema or template with a sample payload exists for this document type yet — register one from Message Schemas or Templates, or paste a real source payload below.</span>
            </div>
          )}
        </div>

        <div className="full-width tabs" role="tablist" aria-label="Payload entry mode">
          <button type="button" role="tab" aria-selected={mode === 'guided'} className={mode === 'guided' ? 'active' : ''} onClick={() => setMode('guided')}>Guided</button>
          <button type="button" role="tab" aria-selected={mode === 'raw'} className={mode === 'raw' ? 'active' : ''} onClick={() => setMode('raw')}>Raw JSON</button>
        </div>

        {mode === 'guided' ? (
          <>
            {parsedPayload && typeof parsedPayload === 'object' ? (
              <GuidedPayloadEditor sample={payloadText} setSample={setPayloadText} format="JSON" />
            ) : (
              <div className="full-width state-card">
                <p>Paste a JSON payload below (or click &quot;Use example payload&quot; above) to enable field-by-field editing.</p>
                <textarea
                  rows={10}
                  value={payloadText}
                  onChange={(event) => setPayloadText(event.target.value)}
                  placeholder="Paste the real source payload for this document."
                />
              </div>
            )}
            <details className="full-width">
              <summary>Preview full payload JSON</summary>
              <PayloadViewer value={previewPayload} filename="payload-preview.json" />
            </details>
          </>
        ) : (
          <label className="full-width">
            <span>Payload JSON</span>
            <textarea required rows={18} value={payloadText} onChange={(event) => setPayloadText(event.target.value)} placeholder="Paste the real source payload for this document." />
          </label>
        )}

        {error ? <div className="form-error full-width">{error}</div> : null}
        <div className="full-width form-actions">
          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Generating...' : 'Generate'}
          </button>
        </div>
      </form>
    </section>
  );
}
